import React, { useEffect, useState, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import "./BlurText.css";

export default function BlurText({
  text = "",
  words,
  className = "",
  fontSize = "inherit",
  fontFamily = "inherit",
  textColor = "inherit"
}) {
  const [hasAnimated, setHasAnimated] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const textWords = useMemo(() => {
    if (words) return words;
    
    const splitWords = text.split(" ");
    const totalWords = splitWords.length;
    
    return splitWords.map((word, index) => {
      const progress = index / totalWords;
      const exponentialDelay = Math.pow(progress, 0.8) * 0.3;
      const baseDelay = index * 0.04; // Faster stagger
      const microVariation = (Math.random() - 0.5) * 0.03;
      
      return {
        text: word,
        duration: 0.8 + Math.cos(index * 0.3) * 0.2, // Snappier duration
        delay: baseDelay + exponentialDelay + microVariation,
        blur: 8 + Math.floor(Math.random() * 6), // Slightly less blur for faster readability
        scale: 0.95 + Math.sin(index * 0.2) * 0.03
      };
    });
  }, [text, words]);

  useEffect(() => {
    if (inView && !hasAnimated) {
      const timer = setTimeout(() => {
        setHasAnimated(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [inView, hasAnimated]);

  return (
    <span 
      ref={ref} 
      className={`blur-text-wrapper ${className}`} 
      style={{ fontSize, fontFamily, color: textColor, display: 'inline-block' }}
    >
      {textWords.map((word, index) => {
        const wordLower = word.text.toLowerCase();
        const isHighlight = wordLower.includes("techzetastudio.com") || wordLower.includes("techzeta.com");
        const isDivider = word.text === "|";
        
        return (
          <span
            key={index}
            className={`blur-text-word ${hasAnimated ? 'is-visible' : ''}`}
            style={{
              transitionDuration: `${word.duration}s`,
              transitionDelay: `${word.delay}s`,
              transitionTimingFunction: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              filter: hasAnimated 
                ? 'blur(0px) brightness(1)' 
                : `blur(${word.blur}px) brightness(0.6)`,
              transform: hasAnimated 
                ? 'translateY(0) scale(1) rotateX(0deg)' 
                : `translateY(20px) scale(${word.scale || 1}) rotateX(-15deg)`,
              marginRight: '0.35em',
              display: 'inline-block',
              willChange: 'filter, transform, opacity',
              transformStyle: 'preserve-3d',
              backfaceVisibility: 'hidden',
              textShadow: hasAnimated 
                ? '0 2px 8px rgba(255,255,255,0.1)' 
                : '0 0 40px rgba(255,255,255,0.4)',
              opacity: hasAnimated ? 1 : 0,
              // Custom highlights
              color: isHighlight ? "#fff" : isDivider ? "rgba(255,255,255,0.3)" : "inherit",
              fontWeight: isHighlight ? "600" : "inherit"
            }}
          >
            {word.text}
          </span>
        );
      })}
    </span>
  );
}
